import React, { useRef } from 'react';
import Form from 'react-bootstrap/Form';
import { FormControlProps } from 'react-bootstrap/FormControl';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import { BasicLeadFormModel } from '../../models/globals';
import Exclamation from '../../svgs/exclamation.svg';
import { DefaultValueCheckbox } from './DefaultValueCheckbox';
import ReCAPTCHA from 'react-google-recaptcha';

const leadFormHeading = "You've got questions. We're here to help.";

const forceValidationForIE: FormControlProps['onChange'] = ({ currentTarget: input }) => {
    input.classList.toggle(':invalid', !input.checkValidity());
};

type FormName = 'financial' | 'government' | 'military' | 'mycaa' | 'product';

type FormState = 'initial' | 'invalid' | 'pre-submit' | 'submitting';

interface LeadFormProps extends BasicLeadFormModel {
    formName: FormName;
    onSubmit?(event: React.FormEvent<HTMLFormElement>): void;
    onLeadCreationError?(): void;
    onLeadCreationSuccess?(): void;
}

const LeadForm: React.FC<LeadFormProps> = ({
    formName,
    leadAttributes: { autoCapturedAttributes },
    leadCreationApiPath,
    isRecaptchaEnabled,
    onSubmit,
    onLeadCreationError,
    onLeadCreationSuccess,
    siteKey,
    baseUrl,
}) => {
    const [formState, setFormState] = React.useState<FormState>('initial');
    const inputsDisabled = formState === 'submitting';
    const inputsReadOnly = inputsDisabled || formState === 'pre-submit';

    const recaptchaRef = useRef<ReCAPTCHA>(null);

    const CreateLeadApi = (leadCreationApiPath: string, data: string, formData: any): boolean => {
        const queryParams = new URLSearchParams([...(new FormData(formData) as any)]);
        const redirectURL = leadCreationApiPath.split('=')[1].replaceAll('%2F', '/');
        queryParams.append('token', data);
        fetch(leadCreationApiPath, {
            method: 'POST',
            body: queryParams,
        })
            .then(() => {
                if (formName === 'product') {
                    onLeadCreationSuccess?.();
                } else {
                    window.location.href = `${baseUrl}${redirectURL}`;
                }
                return true;
            })
            .catch(() => {
                if (formName === 'product') {
                    onLeadCreationError?.();
                }
            });
        return true;
    };

    return (
        <>
            {/* For 'product' forms, see the modal header in CtpLeadModal.tsx */}
            {formName !== 'product' && <h2 className="font-weight-semi-bold h3 my-4">{leadFormHeading}</h2>}
            <p className={formName === 'product' ? 'mb-3 mt-n3' : 'my-3'}>
                {{
                    financial: 'Questions about financial assistance? ',
                    government: 'Questions about financial assistance? ',
                    military: 'Questions about military benefits? ',
                    mycaa: 'Questions about military benefits? ',
                    product: '',
                }[formName] + 'An enrollment specialist will contact you with more information.'}
            </p>
            <Form
                action={!isRecaptchaEnabled ? leadCreationApiPath : undefined}
                aria-label="request information"
                className="lead-form"
                method={!isRecaptchaEnabled ? 'post' : undefined}
                noValidate // stop browser's auto check, since we want to use bootstrap validation
                onSubmit={(event) => {
                    const form = event.currentTarget;

                    Array.from(form.elements).forEach((element) => {
                        const input = element.nodeName === 'INPUT' && (element as HTMLInputElement);
                        if (input && input.type !== 'hidden') {
                            input.value = input.value.trim();
                        }
                    });

                    if (form.checkValidity()) {
                        if (!isRecaptchaEnabled) {
                            //Make inputs readonly immediately (useful for IE where formdata event is not supported)
                            setFormState('pre-submit');
                            //Make inputs disabled only after the formdata event, else the input values will be excluded
                            form.addEventListener('formdata', function setSubmitting() {
                                setFormState('submitting');
                                form.removeEventListener('formdata', setSubmitting);
                            });
                            onSubmit?.(event);
                        } else {
                            recaptchaRef.current?.executeAsync().then((data) => {
                                const formData = event.target as HTMLFormElement;
                                if (data !== null) {
                                    CreateLeadApi(leadCreationApiPath, data, formData);
                                }
                                return true;
                            });
                            event.preventDefault();
                        }
                    } else {
                        event.preventDefault();
                        setFormState('invalid');
                    }
                }}
                validated={formState === 'invalid'}
            >
                <fieldset disabled={inputsDisabled}>
                    <Row>
                        <Col sm={6}>
                            <Form.Group className="mb-3" controlId="formFirstName">
                                <Form.Label className="font-weight-semi-bold">First name *</Form.Label>
                                <Form.Control
                                    maxLength={50}
                                    name="first_name"
                                    onChange={forceValidationForIE}
                                    pattern=".*\S+.*"
                                    readOnly={inputsReadOnly}
                                    required
                                    type="text"
                                />
                                <Form.Control.Feedback type="invalid">
                                    <Exclamation aria-label="error icon" className="size-4" />
                                    Enter your first name.
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col sm={6}>
                            <Form.Group className="mb-3" controlId="formLastName">
                                <Form.Label className="font-weight-semi-bold">Last name *</Form.Label>
                                <Form.Control
                                    maxLength={50}
                                    name="last_name"
                                    onChange={forceValidationForIE}
                                    pattern=".*\S+.*"
                                    readOnly={inputsReadOnly}
                                    required
                                    type="text"
                                />
                                <Form.Control.Feedback type="invalid">
                                    <Exclamation aria-label="error icon" className="size-4" />
                                    Enter your last name.
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={6}>
                            <EmailInput readOnly={inputsReadOnly} />
                        </Col>
                        <Col sm={6}>
                            <Form.Group className="mb-3" controlId="formPhone">
                                <Form.Label className="font-weight-semi-bold">Phone number *</Form.Label>
                                <Form.Control
                                    maxLength={50}
                                    name="phone"
                                    onChange={forceValidationForIE}
                                    pattern=".*\S+.*"
                                    readOnly={inputsReadOnly}
                                    required
                                    type="tel"
                                />
                                <Form.Control.Feedback type="invalid">
                                    <Exclamation aria-label="error icon" className="size-4" />
                                    Enter your phone number.
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>
                    {formName === 'military' && (
                        <Row>
                            <Col sm={6}>
                                <Form.Group className="mb-3" controlId="formMilitaryBranch">
                                    <Form.Label className="font-weight-semi-bold">Military branch *</Form.Label>
                                    <Form.Control
                                        as="select"
                                        defaultValue=""
                                        name="Military_Branch__c"
                                        onChange={forceValidationForIE}
                                        readOnly={inputsReadOnly}
                                        required
                                    >
                                        <option value="" disabled></option>
                                        <option value="Army">Army</option>
                                        <option value="Air Force">Air Force</option>
                                        <option value="Navy">Navy</option>
                                        <option value="Marines">Marines</option>
                                        <option value="Coast Guard">Coast Guard</option>
                                    </Form.Control>
                                    <Form.Control.Feedback type="invalid">
                                        <Exclamation aria-label="error icon" className="size-4" />
                                        Select a military branch.
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col sm={6}>
                                <Form.Group className="mb-3" controlId="formMilitaryBenefit">
                                    <Form.Label className="font-weight-semi-bold">Type of benefit *</Form.Label>
                                    <Form.Control
                                        as="select"
                                        defaultValue=""
                                        name="Military_Benefit__c"
                                        onChange={forceValidationForIE}
                                        readOnly={inputsReadOnly}
                                        required
                                    >
                                        <option value="" disabled></option>
                                        <option value="Tuition Assistance">Tuition Assistance</option>
                                        <option value="MyCAA">MyCAA</option>
                                        <option value="Other/Unknown">Other/Unknown</option>
                                    </Form.Control>
                                    <Form.Control.Feedback type="invalid">
                                        <Exclamation aria-label="error icon" className="size-4" />
                                        Select a benefit.
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                    )}
                    <Row className="mt-3 small">
                        <Col>
                            By submitting this form, you acknowledge that you are 16 years of age or older and agree to
                            be contacted by email, telephone, or text for the specific purpose you have submitted your
                            information above.
                        </Col>
                    </Row>
                    <Row className="mt-2 small">
                        <Col>
                            <Form.Group className="mb-3" controlId="formEmailOptIn">
                                <Form.Check custom>
                                    <DefaultValueCheckbox // We need an explicit value sent for each checkbox state, for legal reasons
                                        className="mt-0"
                                        defaultChecked={false}
                                        name="Email_Opt_Out_Tech_Forms__c"
                                        values={{ checked: '0', unchecked: '1' }}
                                    />
                                    <Form.Check.Label className="pl-2 font-weight-bold">
                                        Email me about promotions and special offers.
                                    </Form.Check.Label>
                                </Form.Check>
                            </Form.Group>
                        </Col>
                    </Row>
                    {/* Add auto-captured values as hidden form fields */}
                    {autoCapturedAttributes.map(
                        ({ name, value }) =>
                            value != null && <input key={name} name={name} type="hidden" value={value} />,
                    )}
                    <Row className="justify-content-center mt-3">
                        <Col sm="auto">
                            <Button block size="lg" type="submit">
                                REQUEST INFORMATION
                            </Button>
                            {isRecaptchaEnabled && (
                                <div>
                                    <p className="recaptcha-text">
                                        This site is protected by reCAPTCHA and the Google{' '}
                                        <a
                                            className="recaptcha-policy-text"
                                            href="https://policies.google.com/privacy"
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            Privacy Policy
                                        </a>{' '}
                                        and
                                        <a
                                            className="recaptcha-policy-text"
                                            href="https://policies.google.com/terms"
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            {' '}
                                            Terms of Service
                                        </a>{' '}
                                        apply.
                                    </p>
                                    <ReCAPTCHA
                                        className="g-recaptcha"
                                        size="invisible"
                                        ref={recaptchaRef}
                                        sitekey={siteKey}
                                    />
                                </div>
                            )}
                        </Col>
                    </Row>
                </fieldset>
            </Form>
        </>
    );
};

interface EmailInputProps {
    readOnly: boolean;
}

type EmailFeedbackMessage = 'Enter your email.' | 'Enter a valid email address.';

const EmailInput: React.FC<EmailInputProps> = ({ readOnly }) => {
    const [feedbackMessage, setFeedbackMessage] = React.useState<EmailFeedbackMessage>('Enter your email.');

    return (
        <Form.Group className="mb-3" controlId="email">
            <Form.Label className="font-weight-semi-bold">Email *</Form.Label>
            <Form.Control
                maxLength={320}
                name="email"
                onChange={(event) => {
                    forceValidationForIE(event);
                    setFeedbackMessage(
                        event.currentTarget.validity.valueMissing
                            ? 'Enter your email.'
                            : 'Enter a valid email address.',
                    );
                }}
                readOnly={readOnly}
                required
                type="email"
            />
            <Form.Control.Feedback type="invalid">
                <Exclamation aria-label="error icon" className="size-4" />
                {feedbackMessage}
            </Form.Control.Feedback>
        </Form.Group>
    );
};

export { FormName, LeadForm, leadFormHeading };
